import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtTime, short } from "@/lib/api";

const SourceBadge = ({ source }) => (
  <span className={`font-mono text-[11px] px-2 py-0.5 rounded border ${source === "auto" ? "border-cyan-500/30 text-cyan-300 bg-cyan-500/10" : "border-slate-600 text-slate-300 bg-slate-800/60"}`}>
    {source === "auto" ? "detected" : "by hand"}
  </span>
);

const AnchorBadge = ({ seq }) =>
  seq ? (
    <span className="font-mono text-[11px] px-2 py-0.5 rounded border border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/10">anchor #{seq}</span>
  ) : (
    <span className="font-mono text-[11px] px-2 py-0.5 rounded border border-amber-500/20 text-amber-300/80">waiting</span>
  );

export const CHECK_STYLES = {
  verified: ["verified", "border-emerald-500/30 text-emerald-300 bg-emerald-500/10"],
  failed: ["impersonator", "border-rose-500/40 text-rose-300 bg-rose-500/10"],
  undeclared: ["undeclared", "border-sky-500/40 text-sky-300 bg-sky-500/10"],
  unverifiable: ["unverified", "border-slate-600 text-slate-400"],
};

export const CheckBadge = ({ status, testId }) => {
  if (!status) return <span className="text-xs text-slate-600">—</span>;
  const [label, cls] = CHECK_STYLES[status] || CHECK_STYLES.unverifiable;
  return <span data-testid={testId} className={`font-mono text-[11px] px-2 py-0.5 rounded border whitespace-nowrap ${cls}`}>{label}</span>;
};

export const LedgerTable = ({ rows, onSelect }) => (
  <div className="rounded-md border border-[#222D3D] bg-[#0F141C] overflow-x-auto" data-testid="ledger-table">
    <Table>
      <TableHeader>
        <TableRow className="border-[#222D3D] hover:bg-transparent">
          {["#", "Arrived", "Identity", "Origin check", "Source", "Entry hash", "Bitcoin"].map((h) => (
            <TableHead key={h} className="text-[11px] uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 && (
          <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-14" data-testid="ledger-empty">No arrivals recorded yet.</TableCell></TableRow>
        )}
        {rows.map((r) => (
          <TableRow key={r.seq} onClick={() => onSelect(r)} className="border-[#222D3D] hover:bg-[#141A24] transition-colors cursor-pointer" data-testid={`ledger-table-row-${r.seq}`}>
            <TableCell className="font-mono text-xs text-slate-500">{r.seq}</TableCell>
            <TableCell className="font-mono text-xs text-slate-300 whitespace-nowrap">{fmtTime(r.arrived_at)}</TableCell>
            <TableCell className="min-w-[220px]">
              <p className="text-slate-100 font-medium">{r.identity}</p>
              <p className="text-xs text-slate-500">
                {r.details?.direction && <span className={`font-mono mr-1.5 ${r.details.direction === "inbound" ? "text-cyan-400" : "text-amber-300"}`}>{r.details.direction === "inbound" ? "↘ in" : "↗ out"}</span>}
                {[r.operator, r.details?.area, r.device && `via ${r.device}`, r.details?.destination || r.site].filter(Boolean).join(" · ") || "—"}
              </p>
            </TableCell>
            <TableCell><CheckBadge status={r.verification?.status} testId={`ledger-check-${r.seq}`} /></TableCell>
            <TableCell><SourceBadge source={r.source} /></TableCell>
            <TableCell><code className="font-mono text-xs text-emerald-400 bg-slate-900/90 px-2 py-1 rounded border border-slate-800 whitespace-nowrap">{short(r.hash)}</code></TableCell>
            <TableCell><AnchorBadge seq={r.anchor_seq} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
